import { useEffect, useState, useRef } from "react";
import { api, money, formatApiErrorDetail, API } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, UploadCloud, ExternalLink, Gavel, Loader2, FileText, LayoutTemplate } from "lucide-react";
import { TenderForm, TENDER_STATUSES, emptyTender } from "@/components/tenders/TenderForm";
import { LetterheadsDialog } from "@/components/tenders/LetterheadsDialog";
import { TenderDocPanel } from "@/components/tenders/TenderDocPanel";

const STATUSES = TENDER_STATUSES;
const STATUS_COLOR = {
  Initiated: "border-zinc-400 text-zinc-600", "In progress": "border-blue-500 text-blue-600",
  "Awaiting Documents": "border-amber-500 text-amber-600", "Documents Received": "border-indigo-500 text-indigo-600",
  Complete: "border-emerald-500 text-emerald-600",
  Identified: "border-zinc-400 text-zinc-600", "In Progress": "border-blue-500 text-blue-600",
  Submitted: "border-indigo-500 text-indigo-600", "Under Evaluation": "border-amber-500 text-amber-600",
  Awarded: "border-green-600 text-green-700", Unsuccessful: "border-red-500 text-red-600",
  Cancelled: "border-zinc-300 text-zinc-400",
};
const DOC_TYPES = ["Tender Document", "Submitted Bid", "Tax Clearance / PIN", "B-BBEE Certificate", "CSD Report", "Company Registration (CIPC)", "Compliance / SBD Forms", "Award Letter", "Other"];
const empty = emptyTender;

export default function Tenders() {
  const { can } = useAuth();
  const confirm = useConfirm();
  const editable = can("tenders");
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [uploading, setUploading] = useState(false);
  const [letterheads, setLetterheads] = useState([]);
  const [lhOpen, setLhOpen] = useState(false);
  const fileRef = useRef();
  const loadLetterheads = () => api.get("/letterheads").then(({ data }) => setLetterheads(data)).catch(() => {});

  const load = () => {
    const params = statusFilter !== "all" ? { status: statusFilter } : {};
    api.get("/tenders", { params }).then(({ data }) => setItems(data));
  };
  useEffect(() => { load(); }, [statusFilter]);
  useEffect(() => { loadLetterheads(); }, []);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (t) => { setForm({ ...empty, ...t, department: t.department || t.organ || "Treasury", province: t.province || "Gauteng Province" }); setEditing(t.id); setOpen(true); };

  const save = async () => {
    if (!form.reference || !form.title) return toast.error("Reference and title are required");
    const payload = { ...form, bid_amount: Number(form.bid_amount) || 0, validity_days: Number(form.validity_days) || 0, markup_pct: Number(form.markup_pct) || 0, organ: form.department };
    try {
      if (editing) await api.put(`/tenders/${editing}`, payload);
      else await api.post("/tenders", payload);
      toast.success(editing ? "Tender updated" : "Tender registered");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (t) => {
    if (!(await confirm({ title: "Delete tender?", description: `Bid ${t.reference} and its records will be removed.`, confirmText: "Delete" }))) return;
    await api.delete(`/tenders/${t.id}`); toast.success("Deleted"); load();
  };

  const openDetail = async (t) => { const { data } = await api.get(`/tenders/${t.id}`); setDetail(data); };

  const setStatus = async (status) => {
    const { data } = await api.patch(`/tenders/${detail.id}/status`, { status });
    setDetail(data); load(); toast.success(`Marked ${status}`);
  };
  const saveReview = async () => {
    const payload = { status: detail.status, review_notes: detail.review_notes || "" };
    if (detail.status === "Awarded" || detail.status === "Unsuccessful") {
      payload.awarded_amount = Number(detail.awarded_amount) || 0;
      payload.awarded_to = detail.awarded_to || "";
    }
    const { data } = await api.patch(`/tenders/${detail.id}/status`, payload);
    setDetail(data); load(); toast.success("Review saved");
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("doc_type", docType); fd.append("title", file.name);
      await api.post(`/tenders/${detail.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get(`/tenders/${detail.id}`);
      setDetail(data);
      toast.success("Document uploaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const delDoc = async (d) => {
    if (!(await confirm({ title: "Remove document?", description: `"${d.title}" will be removed from this tender.`, confirmText: "Remove" }))) return;
    await api.delete(`/tenders/${detail.id}/documents/${d.doc_id}`);
    const { data } = await api.get(`/tenders/${detail.id}`);
    setDetail(data); toast.success("Removed");
  };

  const outcomeBadge = (t) =>
    t.outcome === "successful" ? <Badge className="bg-green-600 text-white">Won</Badge>
      : t.outcome === "unsuccessful" ? <Badge className="bg-primary text-white">Lost</Badge>
      : <Badge variant="outline" className="text-muted-foreground">Pending</Badge>;

  const daysLeft = (cd) => {
    if (!cd) return null;
    const d = Math.ceil((new Date(cd) - new Date()) / 86400000);
    return d;
  };

  return (
    <div>
      <PageHeader title="Tender & Bid Management" subtitle="Track bid documents, closing dates, submissions and award outcomes.">
        {editable && <div className="flex gap-2">
          <Button variant="outline" onClick={() => setLhOpen(true)} data-testid="letterheads-button"><LayoutTemplate className="h-4 w-4 mr-1.5" /> Letterheads</Button>
          <Button onClick={openNew} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="new-tender-button"><Plus className="h-4 w-4 mr-1.5" /> Register Tender</Button>
        </div>}
      </PageHeader>
      <LetterheadsDialog open={lhOpen} onOpenChange={setLhOpen} letterheads={letterheads} reload={loadLetterheads} />

      <div className="mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[220px] h-10 bg-white" data-testid="tender-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-border rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-secondary hover:bg-secondary">
            <TableHead>Bid Reference</TableHead><TableHead>Title</TableHead><TableHead>Department</TableHead>
            <TableHead>Closing Date</TableHead><TableHead className="text-right">Bid Amount</TableHead>
            <TableHead>Status</TableHead><TableHead>Outcome</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((t) => {
              const dl = daysLeft(t.closing_date);
              const closing = t.status !== "Awarded" && t.status !== "Unsuccessful" && t.status !== "Cancelled" && dl !== null && dl <= 7;
              return (
                <TableRow key={t.id} className="cursor-pointer" onClick={() => openDetail(t)} data-testid={`tender-row-${t.reference}`}>
                  <TableCell className="font-mono text-sm font-medium">{t.reference}</TableCell>
                  <TableCell className="max-w-[240px] truncate">{t.title}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t.department || t.organ}</TableCell>
                  <TableCell className="text-sm">
                    {t.closing_date || "—"}
                    {closing && <span className="ml-2 text-xs font-semibold text-primary">{dl < 0 ? "closed" : `${dl}d left`}</span>}
                  </TableCell>
                  <TableCell className="text-right tabular">{t.bid_amount ? money(t.bid_amount) : "—"}</TableCell>
                  <TableCell><Badge variant="outline" className={STATUS_COLOR[t.status]}>{t.status}</Badge></TableCell>
                  <TableCell>{outcomeBadge(t)}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {editable && <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => del(t)} data-testid={`delete-tender-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>}
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No tenders registered yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit Tender" : "Register Tender"}</DialogTitle></DialogHeader>
          <TenderForm form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="save-tender-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / review dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl flex items-center gap-2">
                  <Gavel className="h-5 w-5 text-primary" /> {detail.reference}
                  <Badge variant="outline" className={`ml-1 ${STATUS_COLOR[detail.status]}`}>{detail.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5">
                <div>
                  <div className="font-semibold">{detail.title}</div>
                  <div className="text-sm text-muted-foreground">{detail.department || detail.organ}{detail.province ? ` · ${detail.province}` : ""}{detail.category ? ` · ${detail.category}` : ""}</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <Info label="Type of Document" value={detail.doc_type === "Other" ? detail.doc_type_other : detail.doc_type} />
                  <Info label="Advertised" value={detail.advertised_date} />
                  <Info label="Briefing" value={detail.briefing_mode && detail.briefing_mode !== "None" ? `${detail.briefing_mode} · ${detail.briefing_date || ""} ${detail.briefing_time || ""}` : detail.briefing_date} />
                  <Info label="Closing" value={detail.closing_date ? `${detail.closing_date} ${detail.closing_time || ""}` : ""} />
                  <Info label="Validity" value={detail.validity_days ? `${detail.validity_days} days` : ""} />
                  <Info label="Mark-up" value={detail.markup_pct ? `${detail.markup_pct}%` : ""} />
                  <Info label="Bid Amount" value={detail.bid_amount ? money(detail.bid_amount) : "—"} />
                  {detail.awarded_amount ? <Info label="Awarded Amount" value={money(detail.awarded_amount)} /> : null}
                  {detail.awarded_to ? <Info label="Awarded To" value={detail.awarded_to} /> : null}
                </div>
                {detail.description && <p className="text-sm text-muted-foreground border-l-2 border-border pl-3">{detail.description}</p>}
                <TenderDocPanel tender={detail} letterheads={letterheads} editable={editable} onChanged={() => openDetail(detail)} onManageLetterheads={() => setLhOpen(true)} />

                {/* Documents */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-display font-semibold text-sm uppercase tracking-wide">Bid Documents ({detail.documents?.length || 0})</h4>
                  </div>
                  <div className="space-y-2">
                    {(detail.documents || []).map((d) => (
                      <div key={d.doc_id} className="flex items-center gap-3 border border-border rounded-md px-3 py-2" data-testid="tender-doc">
                        <FileText className="h-4 w-4 text-primary shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{d.title}</div>
                          <div className="text-[11px] text-muted-foreground">{d.doc_type}</div>
                        </div>
                        <a href={`${API}/files/${d.storage_path}`} target="_blank" rel="noreferrer" className="text-primary text-xs font-medium flex items-center gap-1 hover:underline">Open <ExternalLink className="h-3 w-3" /></a>
                        {editable && <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => delDoc(d)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </div>
                    ))}
                    {(detail.documents || []).length === 0 && <p className="text-sm text-muted-foreground">No documents uploaded.</p>}
                  </div>
                  {editable && (
                    <div className="flex items-end gap-2 mt-3">
                      <div className="space-y-1.5 flex-1">
                        <Label className="text-[10px] uppercase text-muted-foreground">Document type</Label>
                        <Select value={docType} onValueChange={setDocType}>
                          <SelectTrigger className="h-9" data-testid="tender-doctype-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <input ref={fileRef} type="file" className="hidden" onChange={(e) => uploadDoc(e.target.files?.[0])} data-testid="tender-file-input" />
                      <Button variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="tender-upload-button">
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UploadCloud className="h-4 w-4 mr-1.5" /> Upload</>}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Review / outcome */}
                {editable && (
                  <div className="border-t border-border pt-4 space-y-3">
                    <h4 className="font-display font-semibold text-sm uppercase tracking-wide">Review & Outcome</h4>
                    <div className="flex flex-wrap gap-2">
                      {STATUSES.map((s) => (
                        <button key={s} onClick={() => setStatus(s)} data-testid={`tender-set-${s.replace(/\s+/g, "-").toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${detail.status === s ? "bg-black text-white border-black" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                    {(detail.status === "Awarded" || detail.status === "Unsuccessful") && (
                      <div className="grid grid-cols-2 gap-3">
                        <F label="Awarded Amount"><Input type="number" value={detail.awarded_amount || ""} onChange={(e) => setDetail({ ...detail, awarded_amount: e.target.value })} /></F>
                        <F label={detail.status === "Awarded" ? "Awarded To (us)" : "Awarded To (competitor)"}><Input value={detail.awarded_to || ""} onChange={(e) => setDetail({ ...detail, awarded_to: e.target.value })} /></F>
                      </div>
                    )}
                    <F label="Review Notes"><Textarea value={detail.review_notes || ""} onChange={(e) => setDetail({ ...detail, review_notes: e.target.value })} rows={3} placeholder="Evaluation notes, scoring, debrief feedback..." data-testid="tender-review-input" /></F>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => { setDetail(null); openEdit(detail); }}>Edit details</Button>
                      <Button onClick={saveReview} className="bg-primary hover:bg-[#C81219] text-white transition-colors" data-testid="save-tender-review">Save Review</Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children, cn = "" }) {
  return <div className={`space-y-1.5 ${cn}`}><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>;
}
function Info({ label, value }) {
  return <div><div className="text-[10px] uppercase text-muted-foreground">{label}</div><div className="font-medium">{value || "—"}</div></div>;
}
